import { useQueryClient } from '@tanstack/react-query'
import { FileX2, RefreshCw, Settings, Upload } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { formatDate } from '@/lib/formatters'
import { CacheKeys } from '../../../../lib/cache'
import { parseError } from '../../../../lib/errors'
import {
  type COIDocument,
  coiDocumentDownloadHref,
  regenerateCOI,
  useCurrentCOIDocuments,
} from '../../../../models/coi'
import { CoiMode } from '../../../../models/insurancePolicy'
import { useCurrentUser } from '../../../../models/user'
import { CertificateSettingsDialog } from './CertificateSettingsDialog'
import { COIAutoRegenerateSwitch } from './COIAutoRegenerateSwitch'
import { UploadCOIDocumentDialog } from './UploadCOIDocumentDialog'

/** "Auto Liability + Cargo", or the fallback for a pre-selection certificate. */
export function selectionLabel(document: COIDocument): string {
  const policies = document.policies ?? []
  if (!policies.length) return 'All policies'
  return policies.map((policy) => policy.vendorName || policy.label).join(' + ')
}

/** Manually issued certificates cannot be rebuilt — the carrier produces them. */
function isManual(document: COIDocument): boolean {
  return (document.policies ?? []).some((policy) => policy.coiMode === CoiMode.MANUAL)
}

export function COIDocumentsTab({ orgId }: { orgId?: string }) {
  const { documents, isLoading } = useCurrentCOIDocuments(orgId)
  const { user } = useCurrentUser({ enabled: true })
  const isCaiAdmin = !!user?.isCaiAdmin
  const queryClient = useQueryClient()
  const [uploadFor, setUploadFor] = useState<COIDocument | null>(null)
  const [settingsFor, setSettingsFor] = useState<COIDocument | null>(null)
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null)

  function invalidate() {
    queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_DOCUMENTS_CURRENT] })
    queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_DOCUMENT_LATEST] })
    queryClient.invalidateQueries({ queryKey: [CacheKeys.COI_CERTIFICATE_HOLDERS] })
  }

  async function handleRegenerate(document: COIDocument) {
    setRegeneratingId(document.id)
    try {
      await regenerateCOI(document.id, orgId)
      invalidate()
      toast.success('Regenerating every certificate for this organization')
    } catch (err) {
      toast.error(parseError(err))
    } finally {
      setRegeneratingId(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-10 w-full" />
      </div>
    )
  }

  if (!documents.length) {
    return (
      <div className="flex flex-col items-center justify-center gap-4 py-20">
        <FileX2 className="text-muted-foreground size-12" />
        <h2 className="text-xl font-semibold">No certificates generated yet</h2>
        <p className="text-muted-foreground text-sm">
          Once a policy is active, a certificate is generated for it — and one for each combination of
          policies.
        </p>
      </div>
    )
  }

  return (
    <>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>Policies</TableHead>
            <TableHead>Version</TableHead>
            <TableHead>Last generated</TableHead>
            {isCaiAdmin && <TableHead>Auto</TableHead>}
            <TableHead className="text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {documents.map((document) => (
            <TableRow key={document.id}>
              <TableCell>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium">{selectionLabel(document)}</span>
                  {(document.policies?.length ?? 0) > 1 && (
                    <Badge variant="outline" className="text-xs">
                      Combined
                    </Badge>
                  )}
                </div>
              </TableCell>
              <TableCell>v{document.version}</TableCell>
              <TableCell>{formatDate(document.dateUpdated || document.dateCreated)}</TableCell>
              {isCaiAdmin && (
                <TableCell>
                  {/* A manual policy's certificate is never generated, so there
                      is nothing to stop regenerating. */}
                  {!isManual(document) && (
                    <COIAutoRegenerateSwitch document={document} orgId={orgId} onChanged={invalidate} />
                  )}
                </TableCell>
              )}
              <TableCell>
                <div className="flex items-center justify-end gap-2">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button variant="link" size="sm" asChild>
                        <a
                          href={coiDocumentDownloadHref(document.id, orgId)}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View
                        </a>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Open this certificate PDF</TooltipContent>
                  </Tooltip>
                  {isCaiAdmin && (
                    <>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            aria-label="Certificate settings"
                            onClick={() => setSettingsFor(document)}
                          >
                            <Settings className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Description of operations and vehicle schedule</TooltipContent>
                      </Tooltip>
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            variant="outline"
                            size="icon"
                            aria-label="Upload a replacement certificate"
                            onClick={() => setUploadFor(document)}
                          >
                            <Upload className="size-4" />
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Upload a replacement certificate</TooltipContent>
                      </Tooltip>
                      {/* Nothing to regenerate from: a manual policy's certificate
                          is issued by the carrier and uploaded by hand. */}
                      {!isManual(document) && (
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="outline"
                              size="icon"
                              aria-label="Regenerate certificates"
                              disabled={regeneratingId === document.id}
                              onClick={() => handleRegenerate(document)}
                            >
                              <RefreshCw
                                className={regeneratingId === document.id ? 'size-4 animate-spin' : 'size-4'}
                              />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent>
                            {regeneratingId === document.id
                              ? 'Regenerating…'
                              : 'Regenerate from the latest policy documents'}
                          </TooltipContent>
                        </Tooltip>
                      )}
                    </>
                  )}
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <UploadCOIDocumentDialog
        document={uploadFor}
        orgId={orgId}
        onOpenChange={(open) => !open && setUploadFor(null)}
        onSuccess={invalidate}
      />

      <CertificateSettingsDialog
        document={settingsFor}
        orgId={orgId}
        onOpenChange={(open) => !open && setSettingsFor(null)}
        onSaved={invalidate}
      />
    </>
  )
}
